import type { TLShape } from "@tldraw/tlschema";

/** Native workspace documents use a short, server-checked writer lease. */
export const DOCUMENT_LEASE_MS = 60_000;

export function nativeDocument(shape: TLShape | null | undefined) {
  return shape?.type === "document" && shape.meta.nativeDocument === true;
}

export function documentWriter(
  shape: TLShape | null | undefined,
  now = Date.now(),
) {
  if (!nativeDocument(shape)) return null;
  const until = shape!.meta.documentWriterUntil;
  return typeof shape!.meta.documentWriter === "string" &&
    typeof until === "number" &&
    Number.isFinite(until) &&
    until > now
    ? shape!.meta.documentWriter
    : null;
}

const leaseIsValid = (until: unknown, now: number) =>
  typeof until === "number" &&
  Number.isFinite(until) &&
  until > now &&
  until <= now + DOCUMENT_LEASE_MS + 10_000;

export function authorizeNativeDocument(
  prev: TLShape | null,
  next: TLShape | null,
  sessionId: string,
  canWrite: boolean,
  now = Date.now(),
): TLShape | null {
  if (!nativeDocument(prev) && !nativeDocument(next)) return next ?? prev;
  if (!canWrite) return null;
  if (!prev && next?.type === "document") {
    const writer = next.meta.documentWriter;
    const until = next.meta.documentWriterUntil;
    return next.props.source === "" &&
      next.parentId.startsWith("page:") &&
      next.meta.nativeDocument === true &&
      Number.isSafeInteger(next.meta.documentVersion) &&
      Number(next.meta.documentVersion) >= 0 &&
      ((!writer && !until) ||
        (writer === sessionId && leaseIsValid(until, now)))
      ? next
      : null;
  }
  if (prev && !next) return prev;
  if (!prev || !next || prev.type !== "document" || next.type !== "document")
    return null;
  if (
    !nativeDocument(next) ||
    next.parentId !== prev.parentId ||
    next.props.source !== prev.props.source ||
    next.props.source !== ""
  )
    return null;

  const previousVersion = prev.meta.documentVersion;
  const nextVersion = next.meta.documentVersion;
  if (
    !Number.isSafeInteger(previousVersion) ||
    Number(previousVersion) < 0 ||
    !Number.isSafeInteger(nextVersion) ||
    Number(nextVersion) < 0
  )
    return null;
  const contentChanged =
    JSON.stringify(prev.props.richText) !== JSON.stringify(next.props.richText);
  const owner = documentWriter(prev, now);
  const nextOwner = next.meta.documentWriter;
  const nextUntil = next.meta.documentWriterUntil;

  // The final text update and release can arrive as one batched sync record.
  if (owner === sessionId && !nextOwner && !nextUntil)
    return (
      contentChanged
        ? Number(nextVersion) > Number(previousVersion)
        : nextVersion === previousVersion
    )
      ? next
      : null;

  // A short writing session (for example paste then Done) can reach the
  // server as one acquisition + content + release record. Accept it only
  // against the unchanged base while no other session owns the document.
  if (
    !owner &&
    !nextOwner &&
    !nextUntil &&
    contentChanged &&
    next.meta.documentWriterBaseVersion === previousVersion &&
    Number(nextVersion) > Number(previousVersion)
  )
    return next;

  // Acquisition or renewal is serialized by the authoritative room. The
  // SDK ignores an authorizer's returned record for updates, so validate
  // the proposed expiry instead of trying to stamp it here.
  if (nextOwner === sessionId && leaseIsValid(nextUntil, now)) {
    if (owner && owner !== sessionId) return null;
    // An optimistic acquisition can be batched with the first keystrokes.
    // Its base version must still match the authoritative document.
    if (!owner)
      return (!contentChanged && nextVersion === previousVersion) ||
        (next.meta.documentWriterBaseVersion === previousVersion &&
          contentChanged &&
          Number(nextVersion) > Number(previousVersion))
        ? next
        : null;
    return contentChanged
      ? owner === sessionId && Number(nextVersion) > Number(previousVersion)
        ? next
        : null
      : nextVersion === previousVersion
        ? next
        : null;
  }

  // Other editors may move or rename the page, but not replace its text or
  // writer fields while a session owns it.
  return nextOwner === prev.meta.documentWriter &&
    nextUntil === prev.meta.documentWriterUntil &&
    !contentChanged &&
    nextVersion === previousVersion
    ? next
    : null;
}
